import React, { useState, useEffect } from "react";
import Select from "react-select";
import { useNavigate, useLocation } from "react-router-dom";
import Sidebar from "./Sidebar";
import api from "../axiosConfig";
import CustomLoader from "./CustomLoader";
import { clearRecommendationsPageCache } from "../utils/recommendationsPageCache";
import {
  explorationLevelHelperText,
  explorationSliderFillPercent,
  explorationSliderLabelClass,
} from "../utils/explorationLevelHelperText";
import "../styles/explorationSlider.css";

const PENDING_INIT_INTERESTS_KEY = "gradconnect_pending_init_interests";
const GUEST_INTERESTS_KEY = "gradconnect_guest_research_interests";

/** Must match backend Users_States.APPLICATION_TIMELINE_CHOICES */
const APPLICATION_TIMELINE_OPTIONS = [
  { value: "this_year", label: "This year (applications due in the next few months)" },
  { value: "next_year", label: "Next year" },
  { value: "two_plus_years", label: "In 2+ years" },
  { value: "exploring", label: "Just exploring / not sure yet" },
];

const InitializeRecommendations = () => {
  const [researchInterests, setResearchInterests] = useState("");
  const [applicationTimeline, setApplicationTimeline] = useState("");
  const [majorFilters, setMajorFilters] = useState([]);
  const [schoolFilters, setSchoolFilters] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [majorOptions, setMajorOptions] = useState([]);
  const [schoolOptions, setSchoolOptions] = useState([]);
  /** 1 = Focus … 5 = Explore; matches backend recommendation_exploration_level */
  const [explorationLevel, setExplorationLevel] = useState(3);
  const navigate = useNavigate();
  const location = useLocation();

  // Prefill research interests from signup/login navigation or session backup (e.g. refresh)
  useEffect(() => {
    const fromState = location.state?.researchInterests;
    const pending = sessionStorage.getItem(PENDING_INIT_INTERESTS_KEY);
    const guestFallback = sessionStorage.getItem(GUEST_INTERESTS_KEY);
    const text =
      (typeof fromState === "string" && fromState.trim() ? fromState : "") ||
      pending ||
      guestFallback ||
      "";
    if (text.trim()) {
      setResearchInterests(text);
    }
  }, [location.state]);

  // Fetch options for majors and schools
  useEffect(() => {
    const fetchMajorsAndSchools = async () => {
      try {
        const [majorsResponse, schoolsResponse] = await Promise.all([
          api.get("/get-program-disciplines/"),
          api.get("/get-school-names/"),
        ]);

        const majorOptions = majorsResponse.data.program_disciplines.map((discipline) => ({
          value: discipline,
          label: discipline,
        }));
        const schoolOptions = schoolsResponse.data.school_names.map((school) => ({
          value: school,
          label: school,
        }));

        setMajorOptions(majorOptions);
        setSchoolOptions(schoolOptions);
      } catch (error) {
        console.error("Error fetching majors or schools:", error);
        setErrorMessage("Failed to load options for majors or schools. Please try again.");
      }
    };

    fetchMajorsAndSchools();
  }, []);

  const handleInputChange = (e) => {
    setResearchInterests(e.target.value);
  };

  const handleMajorChange = (selectedOptions) => {
    setMajorFilters(selectedOptions);
  };

  const handleSchoolChange = (selectedOptions) => {
    setSchoolFilters(selectedOptions);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!researchInterests.trim() || majorFilters.length === 0 || !applicationTimeline) {
      setErrorMessage(
        "Fields marked with * are required. Please fill in research interests, departments, and when you plan to apply."
      );
      return;
    }

    const majors = majorFilters.map((option) => option.value);
    const schools = schoolFilters.map((option) => option.value); // Empty list if no schools selected

    setLoading(true);

    try {
      const response = await api.post("/initialize-recommendations/", {
        research_interests: researchInterests,
        majors: majors,
        schools: schools,
        application_timeline: applicationTimeline,
        recommendation_exploration_level: explorationLevel,
      });

      if (response.status === 200) {
        sessionStorage.removeItem(PENDING_INIT_INTERESTS_KEY);
        sessionStorage.removeItem(GUEST_INTERESTS_KEY);
        clearRecommendationsPageCache();

        let initialRecommendations = null;
        try {
          const recResponse = await api.get("/retrieve-recommendations/");
          if (Array.isArray(recResponse.data) && recResponse.data.length > 0) {
            initialRecommendations = recResponse.data;
          }
        } catch (retrieveErr) {
          console.error("Failed to prefetch recommendations after init:", retrieveErr);
        }

        navigate("/recommendations", {
          replace: true,
          ...(initialRecommendations
            ? { state: { initialRecommendations } }
            : {}),
        });
      }
    } catch (error) {
      if (error.response) {
        setErrorMessage(error.response.data.error || "Failed to submit research interests. Please try again.");
      } else if (error.request) {
        setErrorMessage("No response from the server. Please try again later.");
      } else {
        setErrorMessage("An error occurred: " + error.message);
      }
      setLoading(false); // Reset loading state if there's an error
    }
  };

  return (
<div className="flex h-screen min-h-0 flex-col nav:flex-row">
  {/* Sidebar */}
  <Sidebar />

  {/* Main Content — match Recommendations / Search: padding + overflow */}
  <div className="flex min-h-0 flex-1 flex-col overflow-x-hidden overflow-y-auto p-4 nav:p-6 px-4 nav:px-8">
    {loading ? (
      <div className="flex h-full flex-col items-center justify-center">
        <CustomLoader />
        <p className="mt-4 text-lg font-semibold text-gray-700">Setting things up...</p>
      </div>
    ) : (
      <div className="mx-auto w-full min-w-0 max-w-3xl rounded-lg bg-white p-4 shadow-md sm:p-6 nav:p-8 md:p-12">
        <h2 className="text-center text-2xl font-semibold text-gray-800 nav:text-3xl">
          Welcome to Grad Connect!
        </h2>
        <p className="mt-2 text-center text-base text-gray-600 nav:text-lg">
          Before we start recommending professors, we need to know a little more about you:
        </p>

        <div className="mt-6">
          <p className="text-left text-base font-semibold text-gray-700 nav:text-lg">
            What are your research interests?
            <span className="ml-0.5 font-semibold text-[#0196BF]" aria-hidden="true">
              *
            </span>
          </p>
          <p className="mb-2 text-left text-sm text-gray-700">
            Enter a few keywords to describe them. Separate multiple interests with commas (e.g.
            &nbsp;NLP, computer vision, robotics) so we can match each area.
          </p>
          <textarea
            id="research-interests-textarea"
            name="researchInterests"
            value={researchInterests}
            onChange={handleInputChange}
            rows="3"  // Reduced the number of rows to make it shorter
            placeholder="Enter your research interests here..."
            required
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0196BF] focus:ring-opacity-50 focus:border-[#0196BF] focus:outline-none hover:border-[#0196BF] transition-all"
          ></textarea>
        </div>

        <div className="mt-6">
          <p className="text-left text-base font-semibold text-gray-700 nav:text-lg">
            How broad would you like recommendations to be?
          </p>
          <p className="mb-2 text-left text-sm text-gray-700">
            If you know what you want to study, choose Focus. If you&apos;d like to see more
            professors with different interests, choose Explore.
          </p>
          <div className="mt-4 mb-0 flex items-center justify-between leading-none text-[10px] font-medium sm:text-[11px]">
            <span className={explorationSliderLabelClass(explorationLevel, "focus")}>
              Focus
            </span>
            <span className={explorationSliderLabelClass(explorationLevel, "explore")}>
              Explore
            </span>
          </div>
          <input
            type="range"
            min={1}
            max={5}
            step={1}
            value={explorationLevel}
            onChange={(e) => setExplorationLevel(Number(e.target.value))}
            className="exploration-range -mt-3 w-full"
            style={{
              "--fill-pct": `${explorationSliderFillPercent(explorationLevel)}%`,
            }}
            aria-label="Recommendation mix from Focus to Explore"
            aria-valuemin={1}
            aria-valuemax={5}
            aria-valuenow={explorationLevel}
          />
          <p className="-mt-1.5 text-center text-xs leading-snug text-gray-500">
            {explorationLevelHelperText(explorationLevel)}
          </p>
        </div>

        <div className="mt-6">
          <p className="text-left text-base font-semibold text-gray-700 nav:text-lg">
            What department(s) are you interested in working with professors in?
            <span className="ml-0.5 font-semibold text-[#0196BF]" aria-hidden="true">
              *
            </span>
          </p>
          <p className="mb-2 text-left text-sm text-gray-700 break-words">
            Professors often work across multiple disciplines, and students may earn their degree in one department
            while collaborating with professors from others (e.g., a robotics student in electrical engineering
            working with a mechanical engineering professor). We recommend including related departments to get the
            best recommendations.
          </p>
          <Select
            id="research-interests-select"
            isMulti
            options={majorOptions}
            value={majorFilters}
            onChange={handleMajorChange}
            placeholder="Select department(s)..."
            className="mb-4"
            required
            isSearchable
            styles={{
              control: (base, state) => ({
                ...base,
                height: '50px', // Increased height of the select box
                padding: '0 8px', // Adjust padding to make it taller
                borderColor: state.isFocused ? '#0196BF' : base.borderColor, // Change border color on focus
                boxShadow: state.isFocused ? '0 0 0 2px rgba(1, 150, 191, 0.5)' : base.boxShadow, // Optional: Add a shadow on focus
                '&:hover': {
                  borderColor: '#0196BF', // Ensure the hover state also has the correct color
                }
              }),
              placeholder: (base) => ({
                ...base,
                textAlign: 'left', // Align the placeholder text to the left
              }),
              option: (base, state) => ({
                ...base,
                backgroundColor: state.isSelected
                  ? '#0196BF' // Selected option background color
                  : state.isFocused
                  ? 'rgba(1, 150, 191, 0.1)' // Hover state background color with opacity
                  : base.backgroundColor,
                color: state.isSelected ? 'white' : base.color, // Text color for selected option
                '&:hover': {
                  backgroundColor: 'rgba(1, 150, 191, 0.3)', // Hover color with opacity
                  color: 'black', // White text color when hovered
                }
              }),
              multiValue: (base) => ({
                ...base,
                backgroundColor: '#f3f4f6', // Light gray background for selected values
                color: '#333', // Black text color for selected values
                borderRadius: '9999px', // Pill shape
                padding: '5px 10px', // Padding for a more compact pill shape
              }),
              multiValueLabel: (base) => ({
                ...base,
                color: '#333', // Black text color for selected values
              }),
              multiValueRemove: (base) => ({
                ...base,
                color: '#333', // Color for the remove button in selected values
                '&:hover': {
                  backgroundColor: 'rgba(1, 150, 191, 0.5)', // Hover color for remove button
                  color: 'white',
                },
              }),
              // Scroll the option list, not the outer menu — maxHeight on `menu` clips the last row
              menuList: (base) => ({
                ...base,
                maxHeight: 220,
                paddingTop: 4,
                paddingBottom: 8,
              }),
            }}
          />
        </div>

        <div className="mt-8">
          <p className="text-left text-base font-semibold text-gray-700 nav:text-lg">
            Would you like us to restrict our recommendations to particular schools?
          </p>
          <p className="mb-2 text-left text-sm text-gray-700 break-words">
            If you know the school(s) you will likely complete your degree and are looking for a professor from one
            of them, select them here. Otherwise, we recommend leaving this field blank. You might discover your
            perfect match exists at a school you never considered.
          </p>
          <Select
            id="school-select"
            isMulti
            options={schoolOptions}
            value={schoolFilters}
            onChange={handleSchoolChange}
            placeholder="All schools"
            className="mb-4"
            isSearchable
            styles={{
              control: (base, state) => ({
                ...base,
                height: '50px', // Increased height of the select box
                padding: '0 8px', // Adjust padding to make it taller
                borderColor: state.isFocused ? '#0196BF' : base.borderColor, // Change border color on focus
                boxShadow: state.isFocused ? '0 0 0 2px rgba(1, 150, 191, 0.5)' : base.boxShadow, // Optional: Add a shadow on focus
                '&:hover': {
                  borderColor: '#0196BF', // Ensure the hover state also has the correct color
                }
              }),
              placeholder: (base) => ({
                ...base,
                textAlign: 'left', // Align the placeholder text to the left
              }),
              option: (base, state) => ({
                ...base,
                backgroundColor: state.isSelected
                  ? '#0196BF' // Selected option background color
                  : state.isFocused
                  ? 'rgba(1, 150, 191, 0.1)' // Hover state background color with opacity
                  : base.backgroundColor,
                color: state.isSelected ? 'white' : base.color, // Text color for selected option
                '&:hover': {
                  backgroundColor: 'rgba(1, 150, 191, 0.3)', // Hover color with opacity
                  color: 'black', // White text color when hovered
                }
              }),
              multiValue: (base) => ({
                ...base,
                backgroundColor: '#f3f4f6', // Light gray background for selected values
                color: '#333', // Black text color for selected values
                borderRadius: '9999px', // Pill shape
                padding: '5px 10px', // Padding for a more compact pill shape
              }),
              multiValueLabel: (base) => ({
                ...base,
                color: '#333', // Black text color for selected values
              }),
              multiValueRemove: (base) => ({
                ...base,
                color: '#333', // Color for the remove button in selected values
                '&:hover': {
                  backgroundColor: 'rgba(1, 150, 191, 0.5)', // Hover color for remove button
                  color: 'white',
                },
              }),
              menuList: (base) => ({
                ...base,
                maxHeight: 220,
                paddingTop: 4,
                paddingBottom: 8,
              }),
            }}
          />
        </div>

        <div className="mt-6">
          <p className="text-left text-base font-semibold text-gray-700 nav:text-lg">
            When are you planning to apply to graduate school?
            <span className="ml-0.5 font-semibold text-[#0196BF]" aria-hidden="true">
              *
            </span>
          </p>
          <p className="mb-2 text-left text-sm text-gray-700">
            This helps us understand where you are in your journey.
          </p>
          <Select
            id="application-timeline"
            options={APPLICATION_TIMELINE_OPTIONS}
            value={APPLICATION_TIMELINE_OPTIONS.find((opt) => opt.value === applicationTimeline) || null}
            onChange={(selectedOption) => setApplicationTimeline(selectedOption ? selectedOption.value : "")}
            placeholder="Select an option..."
            isSearchable={false}
            styles={{
              control: (base, state) => ({
                ...base,
                height: '50px',
                padding: '0 8px',
                borderColor: state.isFocused ? '#0196BF' : base.borderColor,
                boxShadow: state.isFocused ? '0 0 0 2px rgba(1, 150, 191, 0.5)' : base.boxShadow,
                '&:hover': {
                  borderColor: '#0196BF',
                }
              }),
              placeholder: (base) => ({
                ...base,
                textAlign: 'left',
              }),
              valueContainer: (base) => ({
                ...base,
                justifyContent: "flex-start",
              }),
              singleValue: (base) => ({
                ...base,
                textAlign: "left",
                marginLeft: 0,
              }),
              option: (base, state) => ({
                ...base,
                backgroundColor: state.isSelected
                  ? '#0196BF'
                  : state.isFocused
                  ? 'rgba(1, 150, 191, 0.1)'
                  : base.backgroundColor,
                color: state.isSelected ? 'white' : base.color,
                '&:hover': {
                  backgroundColor: 'rgba(1, 150, 191, 0.3)',
                  color: 'black',
                }
              }),
              menuList: (base) => ({
                ...base,
                maxHeight: 220,
                paddingTop: 4,
                paddingBottom: 8,
              }),
            }}
          />
        </div>

        <button
          type="submit"
          onClick={handleSubmit}
          disabled={loading}
          className={`mt-8 nav:mt-12 flex w-full max-w-full items-center justify-center rounded-full bg-[#0196BF] px-6 py-3 text-lg font-bold text-white transition hover:bg-[#017BA3] sm:px-12 sm:py-4 sm:text-xl md:px-24 ${
            loading ? "cursor-not-allowed bg-gray-400" : ""
          }`}
        >
          Submit
        </button>

        {errorMessage && <p className="text-red-500 mt-4 text-left">{errorMessage}</p>}
      </div>
    )}
  </div>
</div>




  );
};

export default InitializeRecommendations;
